import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ADSENSE_CLIENT_ID, WEB_ADS_ENABLED, adsenseSlotId, pageAllowsAds,
} from "../lib/ads";

/**
 * <AdSlot placement="guides_inline" />: only renders if the current
 * page is allow-listed AND ads are enabled AND the user is not on a
 * paid plan. Always labelled "Ad" so it never looks like a ConfirmPh
 * affordance.
 *
 * On web: lazy-loads the AdSense script and pushes the slot.
 * On mobile app: the same component would call a native AdMob bridge
 *   (left as a wrapper for the future Capacitor / TWA build). For now
 *   the mobile branch renders the same web slot inside the WebView,
 *   which is acceptable for a first AdMob-less app submission.
 *
 * If ad IDs are missing, renders a labelled placeholder in development
 * and nothing in production, so the page layout still respects the
 * reserved slot space during design.
 */
export function AdSlot({ placement, label = "Ad", className = "",
                          format = "auto", style = {} }) {
  const location = useLocation();
  const insRef = useRef(null);
  const [pushed, setPushed] = useState(false);

  // Read the current user lazily: App.js stores it in localStorage.
  const user = (() => {
    try {
      const raw = localStorage.getItem("confirmph_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const allowed = pageAllowsAds(location.pathname, user);
  const slotId = adsenseSlotId(placement);
  const liveReady = WEB_ADS_ENABLED && ADSENSE_CLIENT_ID && slotId;

  // Lazy-inject the AdSense loader exactly once.
  useEffect(() => {
    if (!allowed || !liveReady) return;
    const SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    if (!document.querySelector(`script[src^="${SRC.split("?")[0]}"]`)) {
      const s = document.createElement("script");
      s.async = true;
      s.crossOrigin = "anonymous";
      s.src = SRC;
      document.head.appendChild(s);
    }
  }, [allowed, liveReady]);

  // Push the slot once the <ins> mounts.
  useEffect(() => {
    if (!allowed || !liveReady || pushed) return;
    if (!insRef.current) return;
    try {
      // window.adsbygoogle is created by the loader script.
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      setPushed(true);
    } catch {
      /* swallow: AdSense will retry on its own */
    }
  }, [allowed, liveReady, pushed]);

  if (!allowed) return null;

  // Dev placeholder if env not configured, so designers can see the slot.
  if (!liveReady) {
    if (process.env.NODE_ENV !== "production") {
      return (
        <div data-testid={`ad-slot-${placement}-placeholder`}
              className={`my-3 border border-dashed border-slate-300 rounded-md bg-slate-50 text-slate-400 text-xs flex items-center justify-center ${className}`}
              style={{ minHeight: 90, ...style }}>
          {label} · placement <code className="ml-1">{placement}</code> · slot unconfigured
        </div>
      );
    }
    return null;
  }

  return (
    <div data-testid={`ad-slot-${placement}`} className={`my-3 ${className}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
        {label}
      </div>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", ...style }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}

export default AdSlot;
